// Wires the relay connection (useRelayConnection.ts), the wire protocol
// (syncMessage.ts), the interpolator (engine), host election (engine), and
// a session's ClientLogicRuntime together into the actual sync engine
// (docs/multiplayer-sync/roadmap.md Phases 2-3). Nothing here talks to the
// relay socket directly - that stays useRelayConnection's job; this hook
// only decides what to do with what it hands back.
//
// What this owns:
//  - Host election (3.2 item 1): re-derived from relay presence on every
//    change via `isElectedHost` (see `resolveIsHost`). `isHost` is
//    `undefined` (deferring to `ClientLogicRuntime`'s single-player-safe
//    `true` default) only when there is no relay to ever elect a host
//    against at all - no `gameId`, this environment has none deployed
//    (`relayStatus === 'unavailable'`), or a real connection permanently
//    gave up retrying (`relayStatus === 'failed'` - see `RelayStatus`'s own
//    doc comment; a client must not be stuck reporting `isHost: false`
//    forever just because the relay it would have elected against is gone
//    for good). Whenever a relay connection is genuinely expected, `isHost`
//    is `false` from the very first render, even before `relayStatus` has
//    had a chance to leave 'idle' - GameRuntime constructs its
//    ClientLogicRuntime and starts calling tick() on the very next
//    animation frame, well before the relay connection (let alone election)
//    can resolve, so defaulting to host here even briefly would let every
//    joining client, not just the actual elected host, fire the
//    once-per-session round-start locally - exactly the "unsynchronised
//    executor" problem this whole roadmap exists to fix. `gameId` is known
//    synchronously (it's already part of the loaded publish payload
//    GameRuntime itself waits on), so this has no timing race the way
//    waiting on `relayStatus` alone would.
//  - The local player's own transform broadcast (3.1): `reportLocalTransform`
//    is called every frame (GameRuntime's `onPlayerTransform`) but only
//    actually sends at most once per TRANSFORM_BROADCAST_INTERVAL_MS, the
//    same "throttled by wall-clock time, not frame rate" pattern
//    `ClientLogicRuntime`'s own periodic `tick` event already uses.
//  - Buffering incoming `transform` messages into one shared `Interpolator`
//    instance, keyed per source id, pruned when a player leaves presence.
//  - The host's shared-effect broadcast (3.2 item 2): `onSharedEffect` is
//    wired to `GameRuntime`'s own prop of the same name, broadcasting a
//    `logic-effect` message (the effect converted back to a resendable
//    shape isn't needed here - the effect itself is what's sent) plus a
//    `logic-snapshot` safety-net resync, both host-only. A second, genuinely
//    periodic `logic-snapshot` broadcast (independent of any effect firing)
//    covers the case `onSharedEffect` alone cannot: a shared state change
//    that produces no `LogicEffect` at all (`set-variable`/`add-variable` -
//    see evaluator.ts's own `executeAction`), which would otherwise never
//    reach the wire until an unrelated effect happens to piggyback it.
//  - Applying incoming `logic-effect`/`logic-snapshot` messages on a
//    non-host client (3.2 item 3), and the relay's late-join catch-up frame
//    (3.5) on any client, host or not - seeding this session's own shared
//    state from whatever the last host last knew, before this session's
//    own host status (if elected) starts producing anything new. Only
//    applied when the message's sender is genuinely the currently-elected
//    host per this client's own `electHost` (a stale/non-host sender must
//    not be able to push shared state), and only once per sender/seq (a
//    host-election flicker replaying an already-applied message must not
//    double-apply a non-idempotent effect like an item grant).
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { MutableRefObject } from 'react';

import {
  Interpolator,
  electHost,
  isElectedHost,
  logicEffectOriginPlayerId,
  logicEffectToAction,
  type ClientLogicRuntime,
  type InterpolatedTransform,
  type LogicEffect,
} from '../../../engine';
import type { RelayInitialState, RelayMessage, RelayPresenceEntry, RelayStatus } from './useRelayConnection';
import {
  isSyncMessage,
  logicEffectSyncMessage,
  logicSnapshotSyncMessage,
  transformSyncMessage,
  type TransformSyncMessage,
} from './syncMessage';

/** At most once per this many ms, matching roadmap 3.1's own starting
 * point ("~150 ms (~6.6 Hz)") - see that section for the throttle-sizing
 * reasoning this interval was chosen against. */
const TRANSFORM_BROADCAST_INTERVAL_MS = 150;

/** The shared `Interpolator`'s own render delay, tied directly to
 * `TRANSFORM_BROADCAST_INTERVAL_MS` above rather than left to
 * `Interpolator`'s own `DEFAULT_DELAY_MS` - the two must never be able to
 * silently drift apart again (see `interpolator.ts`'s own doc comment for
 * what happens when the delay is not comfortably above the broadcast
 * interval: a deterministic freeze-then-jump stutter every single broadcast
 * cycle, even under perfect network conditions). One full broadcast
 * interval of margin, on top of the interval itself, covers real network
 * jitter beyond the perfect-conditions case. */
const INTERPOLATION_DELAY_MS = TRANSFORM_BROADCAST_INTERVAL_MS + 100;

/** How often (ms) a Logic host broadcasts a full `logic-snapshot` resync
 * regardless of whether any effect actually fired - "every few seconds
 * while host". This is what makes the resync genuinely periodic rather than
 * purely piggybacked on `onSharedEffect`'s own per-effect send: a variable
 * that only ever changes via `set-variable`/`add-variable` (including the
 * built-in health=100 seed rule, `clientLogicRuntime.ts`'s own
 * `DEFAULT_SEED_RULES`) produces no `LogicEffect` at all
 * (evaluator.ts's own `executeAction`), so it would otherwise never reach
 * the wire until an unrelated effect happened to fire alongside it - for a
 * session with no other Logic activity, that could be never. This interval
 * guarantees every shared state change reaches other clients within a
 * bounded time regardless. */
const SHARED_SNAPSHOT_INTERVAL_MS = 3000;

/** Whether this client should currently act as Logic host, before
 * `useMultiplayerSync` even has a `ClientLogicRuntime` to drive - pulled
 * out as its own pure function (rather than inlined in the hook) so this
 * correctness-critical decision has a real, direct unit test rather than
 * relying on rendering the whole hook. See the hook's own banner comment
 * for the full reasoning; in short, only the cases where there is no relay
 * to ever elect a host against at all are allowed to defer to
 * `ClientLogicRuntime`'s own single-player-safe `true` default (returning
 * `undefined` here): no `gameId` at all, `relayStatus === 'unavailable'`
 * (this environment has no relay deployed, ever - `RelayStatus`'s own doc
 * comment), and `relayStatus === 'failed'` (a real relay connection was
 * attempted and has permanently given up retrying - also `RelayStatus`'s
 * own doc comment). Every other case (including 'closed', which may still
 * be retrying) must resolve to a real `boolean`, `false` until election
 * actually says otherwise, never assumed true while still connecting. */
export function resolveIsHost(
  gameId: string | undefined,
  relayStatus: RelayStatus,
  presence: readonly RelayPresenceEntry[],
  localPlayerId: string | undefined,
): boolean | undefined {
  if (gameId === undefined || relayStatus === 'unavailable' || relayStatus === 'failed') return undefined;
  if (relayStatus !== 'open') return false;
  return isElectedHost(presence, localPlayerId);
}

/** True when an incoming message's `seq` is not newer than the last one
 * already accepted for its own stream (a source entity's own transform
 * timeline, or a sender's own logic-effect/logic-snapshot stream) - stale
 * (arrived late/out of order) or an exact duplicate/replay, either way not
 * worth applying again. Generic over whichever seq stream a caller tracks -
 * originally written only for transform messages, reused as-is for
 * logic-effect/logic-snapshot dedup below (bug: that path previously had no
 * staleness guard at all, so a host-election flicker re-running this hook's
 * incoming-message effect against an unchanged `messages` entry could
 * double-apply a non-idempotent effect, e.g. an item grant). Pulled out for
 * its own direct unit test, same reasoning as `resolveIsHost`. */
export function isStaleSeq(lastAcceptedSeq: number | undefined, seq: number): boolean {
  return lastAcceptedSeq !== undefined && seq <= lastAcceptedSeq;
}

/** Whether an incoming `logic-effect`/`logic-snapshot` message should be
 * applied at all: never while this client is itself host (a host stays
 * authoritative for its own shared state - see this hook's own banner
 * comment), and only when the message's sender is genuinely the
 * currently-elected host per this client's own independently-computed
 * `electHost` result. Previously the only gate was "not my own broadcast"
 * (`if (isHost) return`), with no check at all that the sender was actually
 * the host - a stale or non-host sender's message would be applied exactly
 * as if it came from the real host. Pulled out for its own direct unit
 * test, same reasoning as `resolveIsHost`/`isStaleSeq`. */
export function shouldApplySharedMessage(
  isHost: boolean | undefined,
  relayPresence: readonly RelayPresenceEntry[],
  senderId: string,
): boolean {
  if (isHost) return false;
  return electHost(relayPresence) === senderId;
}

export interface UseMultiplayerSyncOptions {
  /** The publish payload's own game id - `undefined` before it has loaded
   * or for a publication with no relay concept at all. Used only to tell
   * "no relay, ever" (stay on `ClientLogicRuntime`'s single-player-safe
   * `isHost` default) apart from "a relay connection is expected but not
   * open/elected yet" (must not default to host) - see `isHost`'s own
   * doc comment above for why this can't wait on `relayStatus` alone. */
  gameId: string | undefined;
  relayStatus: RelayStatus;
  relayPresence: RelayPresenceEntry[];
  localPlayerId: string | undefined;
  /** The relay's own queued, not-yet-consumed incoming messages, in arrival
   * order - `useRelayConnection.ts`'s `messages` (a queue, not a single
   * slot, specifically so two messages arriving before React flushes a
   * render are never collapsed into one - see that hook's own doc
   * comment). This hook processes every entry each time this changes, then
   * calls `consumeMessages` for exactly how many it handled. */
  messages: RelayMessage[];
  /** Removes the given number of already-processed messages from the front
   * of `messages` - `useRelayConnection.ts`'s own `consumeMessages`. */
  consumeMessages: (count: number) => void;
  initialState: RelayInitialState;
  send: (payload: unknown) => void;
  /** The same ref `GameRuntime`'s `scriptLogicRef` prop is handed - this
   * hook applies incoming shared state through it, exactly the way the
   * HollowScript scripting capability layer already writes through the
   * same ref for a different reason. */
  logicRuntimeRef: MutableRefObject<ClientLogicRuntime | null>;
}

export interface UseMultiplayerSyncResult {
  /** Pass straight to `GameRuntime`'s `isHost` prop. */
  isHost: boolean | undefined;
  /** Every other connected player's id (relay presence, minus the local
   * player) - pass straight to `GameRuntime`'s `remotePlayerIds` prop
   * (roadmap Phase 4). */
  remotePlayerIds: string[];
  /** Stable across renders - a rendering component samples this every frame
   * (not every React render) via `.sample(Date.now(), localPlayerId)`; see
   * `Interpolator`'s own doc comment. Pass straight to `GameRuntime`'s
   * `remoteInterpolator` prop. */
  interpolator: Interpolator<InterpolatedTransform>;
  /** Pass to `GameRuntime`'s `onPlayerTransform` prop - called every frame,
   * throttled internally to `TRANSFORM_BROADCAST_INTERVAL_MS`. Stable
   * (`useCallback`) across renders that do not actually change what it
   * does, so it never cascades into an unrelated consumer's own effect
   * dependency array re-running on every incoming relay message (see this
   * module's own `onSharedEffect` doc comment for the concrete bug this
   * caused). */
  reportLocalTransform: (transform: InterpolatedTransform) => void;
  /** Pass to `GameRuntime`'s `onSharedEffect` prop. Stable (`useCallback`),
   * same reasoning as `reportLocalTransform`. */
  onSharedEffect: (effect: LogicEffect) => void;
}

export function useMultiplayerSync({
  gameId,
  relayStatus,
  relayPresence,
  localPlayerId,
  messages,
  consumeMessages,
  initialState,
  send,
  logicRuntimeRef,
}: UseMultiplayerSyncOptions): UseMultiplayerSyncResult {
  const isHost = resolveIsHost(gameId, relayStatus, relayPresence, localPlayerId);
  const remotePlayerIds = useMemo(
    () => relayPresence.filter((p) => p.playerId !== localPlayerId).map((p) => p.playerId),
    [relayPresence, localPlayerId],
  );

  const interpolator = useMemo(() => new Interpolator<InterpolatedTransform>({ delayMs: INTERPOLATION_DELAY_MS }), []);

  // The highest `seq` accepted so far per source entity - `TransformSyncMessage.seq`'s
  // own doc comment (syncMessage.ts) promises a receiver can drop an
  // out-of-order frame rather than rendering it; without tracking this, a
  // transform that arrives late (network jitter, a relay Lambda cold start)
  // would still be buffered at whatever wall-clock time it happens to be
  // *received*, rendering as a brief backward rewind even though a newer
  // position had already arrived and rendered.
  const lastSeqRef = useRef<Map<string, number>>(new Map());

  // The highest `seq` accepted so far per *sender* for logic-effect/
  // logic-snapshot messages (both share one counter per broadcasting host -
  // see syncMessage.ts's own doc comment) - the dedup guard those two kinds
  // previously had none of, unlike transform's `lastSeqRef` above. Without
  // this, `isHost` sitting in the incoming-message effect's dependency array
  // meant a host-election flicker (isHost toggling with `messages` itself
  // unchanged) could re-run the effect against the very same
  // already-applied message, double-applying a non-idempotent effect (e.g.
  // an item grant).
  const lastSharedSeqRef = useRef<Map<string, number>>(new Map());

  // A host's own outgoing seq counter, shared across every logic-effect/
  // logic-snapshot message this client ever sends while host (onSharedEffect
  // below, and the periodic broadcast further down) - one monotonic stream,
  // matching `lastSharedSeqRef`'s own per-sender tracking on the receiving
  // side.
  const sharedSeqRef = useRef(0);

  // Drops a departed player's timeline (and its own seq tracking above) so
  // neither lingers forever once they disconnect - Interpolator holds at
  // its latest sample indefinitely otherwise (correct for a player who is
  // simply standing still, wrong for one who has actually left), and a
  // stale lastSeq would otherwise wrongly reject every fresh transform
  // from a reconnecting client, whose own seq counter restarts from 1 on
  // every new session (`reportLocalTransform`'s own `seqRef`) - a presence
  // leave+rejoin (the same connectionId churn a reload/reconnect always
  // causes) is the one unambiguous signal that a restart, not reordering,
  // is why seq just went backwards.
  const knownPresenceIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const current = new Set(relayPresence.map((p) => p.playerId));
    for (const id of knownPresenceIdsRef.current) {
      if (!current.has(id)) {
        interpolator.remove(id);
        lastSeqRef.current.delete(id);
        lastSharedSeqRef.current.delete(id);
      }
    }
    knownPresenceIdsRef.current = current;
  }, [relayPresence, interpolator]);

  // Incoming relay traffic: transform buffering (every client, host or
  // not) and shared-state application (non-host only, and only from the
  // genuinely-elected host - see this hook's own banner comment and
  // `shouldApplySharedMessage`'s own doc comment).
  //
  // Processes every queued message in `messages` (not just the latest one -
  // `useRelayConnection.ts`'s own doc comment on `messages` explains why a
  // single-slot `lastMessage` could silently lose one), then tells the
  // relay hook exactly how many it handled via `consumeMessages` so the
  // queue does not grow unbounded for a long session.
  useEffect(() => {
    if (messages.length === 0) return;
    for (const relayMessage of messages) {
      const message = relayMessage.payload;
      if (!isSyncMessage(message)) continue;

      if (message.kind === 'transform') {
        if (isStaleSeq(lastSeqRef.current.get(message.entityId), message.seq)) continue;
        lastSeqRef.current.set(message.entityId, message.seq);
        interpolator.addSample(message.entityId, Date.now(), { x: message.x, y: message.y, z: message.z, yaw: message.yaw });
        continue;
      }

      const senderId = relayMessage.from.playerId;
      if (!shouldApplySharedMessage(isHost, relayPresence, senderId)) continue;
      if (isStaleSeq(lastSharedSeqRef.current.get(senderId), message.seq)) continue;
      lastSharedSeqRef.current.set(senderId, message.seq);

      const runtime = logicRuntimeRef.current;
      if (!runtime) continue;

      if (message.kind === 'logic-effect') {
        for (const effect of message.effects) {
          const action = logicEffectToAction(effect);
          if (action) runtime.applyRemoteAction(action, logicEffectOriginPlayerId(effect) ?? senderId);
        }
      } else {
        runtime.applyRemoteSnapshot(message);
      }
    }
    consumeMessages(messages.length);
  }, [messages, isHost, relayPresence, interpolator, logicRuntimeRef, consumeMessages]);

  // The relay's late-join catch-up frame (roadmap 3.5): applied whenever a
  // real payload arrives, regardless of this session's own host status -
  // even a freshly-elected host should seed from whatever the previous
  // host last knew rather than starting shared state over from defaults
  // just because election happened to pick it. Fires at most once per
  // connection (initialState only ever moves 'pending' -> 'received' once,
  // per useRelayConnection.ts), so there is no risk of this repeatedly
  // stomping on state a now-authoritative host has since produced.
  useEffect(() => {
    if (initialState.status !== 'received' || initialState.payload === null) return;
    if (!isSyncMessage(initialState.payload) || initialState.payload.kind !== 'logic-snapshot') return;
    logicRuntimeRef.current?.applyRemoteSnapshot(initialState.payload);
  }, [initialState, logicRuntimeRef]);

  // The local player's own transform broadcast (roadmap 3.1) - throttled by
  // wall-clock time, not frame rate, the same pattern
  // ClientLogicRuntime.tick()'s own periodic 'tick' event already uses.
  //
  // `useCallback`, not a plain function recreated every render: this is
  // handed straight through to `GameRuntime`'s `onPlayerTransform` prop,
  // which `playerCallbacks`'s own `useMemo` (GameRuntime.tsx) depends on -
  // an unstable identity here previously meant every incoming relay message
  // (this hook re-rendering for any reason, e.g. `messages` changing ~6+
  // times/second in an active session) produced a *new* `reportLocalTransform`
  // function, cascading into `playerCallbacks` recomputing, which cascaded
  // into `FirstPersonController`'s keyboard-listener effect re-running
  // (its own dependency on `onLockChange`, itself recreated by the same
  // memo), which releases-then-reacquires the shared keyboard listener and
  // clears `keysDown` - silently dropping a currently-held movement key.
  // Depends only on what actually changes this function's behaviour
  // (`relayStatus`, `localPlayerId`, `send`), not on anything that merely
  // happens to change alongside them.
  const lastBroadcastAtRef = useRef(0);
  const seqRef = useRef(0);
  const reportLocalTransform = useCallback(
    (transform: InterpolatedTransform) => {
      if (relayStatus !== 'open' || !localPlayerId) return;
      const now = Date.now();
      if (now - lastBroadcastAtRef.current < TRANSFORM_BROADCAST_INTERVAL_MS) return;
      lastBroadcastAtRef.current = now;
      seqRef.current += 1;
      const message: TransformSyncMessage = transformSyncMessage({ entityId: localPlayerId, seq: seqRef.current, ...transform });
      send(message);
    },
    [relayStatus, localPlayerId, send],
  );

  // The host's shared-effect broadcast (roadmap 3.2 item 2/3.4): a
  // `logic-effect` message narrating exactly what just happened (for a
  // non-host client's own HUD/effect log via applyRemoteAction), plus an
  // immediate `logic-snapshot` resync - a low-latency fast path for the
  // common case, complementing (not replacing) the genuinely periodic
  // broadcast below, which is what actually guarantees a variable-only
  // change (no LogicEffect produced at all - see evaluator.ts's own
  // `executeAction`) reaches the wire within a bounded time even when
  // nothing else happens to fire alongside it. Sent together, every time,
  // rather than trying to detect which variables actually changed - real
  // traffic is low here (round-start once, the periodic tick at most
  // once/second), well within the raised relay throttle; tune from real
  // sessions later, per the roadmap's own deferred cadence question
  // (section 4).
  //
  // `useCallback`, same reasoning as `reportLocalTransform` above - handed
  // straight through to `GameRuntime`'s `onSharedEffect` prop.
  //
  // Deliberately does not check `relayStatus` itself: `onSharedEffect` is
  // handed to `ClientLogicRuntime`'s constructor once and never refreshed
  // afterwards (`logicRuntime`'s own `useMemo` deps in GameRuntime.tsx
  // exclude it on purpose, so a relay connection opening later does not
  // reset already-evaluated Logic state) - closing over this render's
  // `relayStatus` here would permanently freeze it at whatever it was at
  // construction (almost always 'idle', since the relay connects
  // asynchronously after mount), silently dropping every broadcast
  // forever. `send` already no-ops safely against a closed/not-yet-open
  // socket (`useRelayConnection.ts`'s own `send`, itself a stable
  // `useCallback`, reads the live socket off a ref every call), so no
  // separate status gate is needed - or safe - here.
  const onSharedEffect = useCallback(
    (effect: LogicEffect) => {
      sharedSeqRef.current += 1;
      send(logicEffectSyncMessage([effect], sharedSeqRef.current));
      const runtime = logicRuntimeRef.current;
      if (!runtime) return;
      sharedSeqRef.current += 1;
      send(logicSnapshotSyncMessage({ seq: sharedSeqRef.current, ...runtime.remoteSnapshot() }));
    },
    [send, logicRuntimeRef],
  );

  // The genuinely periodic half of the `logic-snapshot` resync (see this
  // module's own banner comment and `SHARED_SNAPSHOT_INTERVAL_MS`'s doc
  // comment): a real wall-clock interval while host, independent of
  // whether `onSharedEffect` ever fires. This is what makes a variable-only
  // change (including the built-in health=100 seed rule) reach other
  // clients within a bounded time rather than "only if something else
  // happens to piggyback it".
  useEffect(() => {
    if (!isHost) return;
    const interval = setInterval(() => {
      const runtime = logicRuntimeRef.current;
      if (!runtime) return;
      sharedSeqRef.current += 1;
      send(logicSnapshotSyncMessage({ seq: sharedSeqRef.current, ...runtime.remoteSnapshot() }));
    }, SHARED_SNAPSHOT_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isHost, send, logicRuntimeRef]);

  return { isHost, remotePlayerIds, interpolator, reportLocalTransform, onSharedEffect };
}
